import { useState, useEffect } from 'react';
import { apiService } from '../services/api.service';
import { categoriesService, CreateCategoryDto } from '../services/categories.service';

export interface Category {
  id: string;
  name: string;
  description: string | null;
  image: string | null;
  sortOrder: number;
  isActive: boolean;
  menuTypeId: string | null;
  tenantId: string;
  createdAt: string;
  updatedAt: string;
  _count?: {
    products: number;
  };
}

export function useCategories(menuTypeId?: string) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Build URL with optional menuTypeId filter
      const url = menuTypeId 
        ? `/categories?menuTypeId=${menuTypeId}` 
        : '/categories';
      
      // apiService.get returns ApiResponse<T> — data lives in response.data
      const response = await apiService.get<Category[]>(url);
      const data: Category[] = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response)
        ? (response as any)
        : [];
      setCategories(
        data
          .filter((cat) => cat.isActive)
          .sort((a, b) => a.sortOrder - b.sortOrder)
      );
    } catch (err: any) {
      setError(err.message || 'Failed to fetch categories');
      console.error('Error fetching categories:', err);
    } finally {
      setLoading(false);
    }
  };

  const createCategory = async (data: CreateCategoryDto) => {
    try {
      await categoriesService.addCategory(data);
      await fetchCategories();
    } catch (err: any) {
      console.error('Error creating category:', err);
      throw err;
    }
  };

  const deleteCategory = async (id: string) => {
    try {
      await categoriesService.deleteCategory(id);
      await fetchCategories();
    } catch (err: any) {
      console.error('Error deleting category:', err);
      throw err;
    }
  };

  useEffect(() => {
    fetchCategories();
  }, [menuTypeId]); // Re-fetch when menuTypeId changes

  return {
    categories,
    loading,
    error,
    createCategory,
    deleteCategory,
    refresh: fetchCategories,
  };
}